import { AccountMenu } from "@/components/layout/AccountMenu";
import {
  NavigationMenu,
  NavigationMenuHeader,
  NavigationMenuHeading,
  NavigationMenuLink,
} from "@/components/layout/NavigationMenu";
import { COUNTRY_DATA } from "@/constants/countries";
import { Box, Select, Text } from "@givenwell/components";
import { ChevronLeftIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { memo } from "react";

export function Sidebar() {
  const navigate = useNavigate();
  const match = useMatches({ select: matches => matches[matches.length - 1]! });
  const route = match.fullPath;
  const params = match.params;
  const search = match.search;
  const countryCode = "countryCode" in params ? params.countryCode : "NZ";

  function handleCountryChange(countryCode: string) {
    navigate({
      to: route as any,
      params: {
        ...params,
        countryCode,
      },
      search: search as any,
    } as any);
  }

  return (
    <NavigationMenu header={<NavigationMenuHeader />} footer={<AccountMenu role="supporter" />}>
      <NavigationMenuLink icon={<ChevronLeftIcon />} activeOptions={{ exact: true }} to="/admin">
        Back
      </NavigationMenuLink>
      <CountryPicker value={countryCode} onChange={handleCountryChange} />

      <NavigationMenuHeading>Supporters</NavigationMenuHeading>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/current-supporters">
        Current Supporters
      </NavigationMenuLink>

      <NavigationMenuHeading>EAP</NavigationMenuHeading>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/eap-arrears">
        Arrears
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/eap-requests">
        Session Requests
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/eap-quality">
        Quality
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/eap-unclosed-sessions">
        Unclosed Sessions
      </NavigationMenuLink>
      <NavigationMenuHeading>Suppliers</NavigationMenuHeading>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/daily-payouts">
        Payouts
      </NavigationMenuLink>
      <NavigationMenuHeading>Members</NavigationMenuHeading>
      <NavigationMenuLink
        params={{ countryCode }}
        search={{ view: "details", from: undefined!, to: undefined! }}
        to="/admin/reports/$countryCode/supporter-sales"
      >
        Sales
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/member-subscriptions">
        Subscriptions
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/card-payments">
        Card Payments
      </NavigationMenuLink>
      <NavigationMenuLink params={{ countryCode }} to="/admin/reports/$countryCode/projected-mrr">
        Projected MRR
      </NavigationMenuLink>
      <Text css={{ fontScale: "2xs", color: colors.text3 }}>
        All reports include data from Givenwell-affiliated Supporters, Suppliers, Members, and bank accounts, unless
        stated otherwise.
      </Text>
    </NavigationMenu>
  );
}

type CountryPickerProps = {
  value: string;
  onChange?: (value: string) => void;
};
const CountryPicker = memo(function CountryPicker(props: CountryPickerProps) {
  const countries = Object.entries(COUNTRY_DATA).map(([code, country]) => ({
    value: code,
    label: country.name,
  }));

  return (
    <Box>
      <Select size="sm" css={{ fontSize: 14 }} value={props.value} onValueChange={props.onChange}>
        {countries.map(country => (
          <option key={country.value} value={country.value}>
            {country.label}
          </option>
        ))}
      </Select>
    </Box>
  );
});
